<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useElementSize } from '@vueuse/core';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CardItem from './CardItem.vue';

const props = defineProps({
  cards: { type: Array, default: () => [] },
  bubbleText: { type: String, default: '' },
});

const carouselTrack = ref(null);
const activeIndex = ref(0);
const canScrollPrev = ref(false);
const canScrollNext = ref(false);
const hasOverflow = ref(false);

const { width: trackWidth } = useElementSize(carouselTrack);

const totalCards = computed(() => props.cards.length);
const previousCardLabel = 'Ver tarjeta anterior';
const nextCardLabel = 'Ver tarjeta siguiente';

const showNavigation = computed(
  () => totalCards.value > 1 && hasOverflow.value
);

const updateScrollState = () => {
  if (!carouselTrack.value) {
    return;
  }

  const { scrollLeft, clientWidth, scrollWidth } = carouselTrack.value;
  hasOverflow.value = scrollWidth > clientWidth + 8;
  canScrollPrev.value = scrollLeft > 8;
  canScrollNext.value = scrollLeft + clientWidth < scrollWidth - 8;

  const cardElements = Array.from(carouselTrack.value.children);
  if (!cardElements.length) {
    activeIndex.value = 0;
    return;
  }

  const viewportCenter = scrollLeft + clientWidth / 2;
  const nearestIndex = cardElements.reduce((closestIndex, element, index) => {
    const cardCenter = element.offsetLeft + element.clientWidth / 2;
    const closestElement = cardElements[closestIndex];
    const closestCenter =
      closestElement.offsetLeft + closestElement.clientWidth / 2;

    return Math.abs(cardCenter - viewportCenter) <
      Math.abs(closestCenter - viewportCenter)
      ? index
      : closestIndex;
  }, 0);

  activeIndex.value = nearestIndex;
};

const scrollToCard = index => {
  if (!carouselTrack.value) {
    return;
  }

  const normalizedIndex = Math.max(0, Math.min(index, totalCards.value - 1));
  const targetCard = carouselTrack.value.children[normalizedIndex];
  if (!targetCard) {
    return;
  }

  carouselTrack.value.scrollTo({
    left: targetCard.offsetLeft,
    behavior: 'smooth',
  });
};

const handleTrackScroll = () => {
  updateScrollState();
};

watch(
  [totalCards, trackWidth],
  async () => {
    await nextTick();
    updateScrollState();
  },
  { immediate: true }
);

onMounted(() => {
  carouselTrack.value?.addEventListener('scroll', handleTrackScroll, {
    passive: true,
  });
  nextTick(updateScrollState);
});

onBeforeUnmount(() => {
  carouselTrack.value?.removeEventListener('scroll', handleTrackScroll);
});
</script>

<template>
  <div class="space-y-3">
    <div class="relative">
      <div
        ref="carouselTrack"
        class="flex gap-3 overflow-x-auto scroll-smooth px-px pb-1 snap-x snap-mandatory"
      >
        <div
          v-for="(card, index) in cards"
          :key="`carousel-card-${index}`"
          class="w-[18rem] max-w-[min(18rem,calc(100vw-8rem))] shrink-0 snap-start"
        >
          <CardItem :card="card" :bubble-text="bubbleText" />
        </div>
      </div>

      <div
        v-if="showNavigation"
        class="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between px-1"
      >
        <button
          type="button"
          class="pointer-events-auto inline-flex size-8 items-center justify-center rounded-full border border-n-weak bg-n-alpha-black1 text-n-slate-12 shadow-sm backdrop-blur disabled:cursor-default disabled:opacity-40"
          :disabled="!canScrollPrev"
          :aria-label="previousCardLabel"
          @click="scrollToCard(activeIndex - 1)"
        >
          <Icon icon="i-lucide-chevron-left" />
        </button>

        <button
          type="button"
          class="pointer-events-auto inline-flex size-8 items-center justify-center rounded-full border border-n-weak bg-n-alpha-black1 text-n-slate-12 shadow-sm backdrop-blur disabled:cursor-default disabled:opacity-40"
          :disabled="!canScrollNext"
          :aria-label="nextCardLabel"
          @click="scrollToCard(activeIndex + 1)"
        >
          <Icon icon="i-lucide-chevron-right" />
        </button>
      </div>
    </div>

    <div v-if="totalCards > 1" class="flex items-center justify-center gap-1.5">
      <button
        v-for="(_, index) in cards"
        :key="`carousel-dot-${index}`"
        type="button"
        class="rounded-full transition-all"
        :class="
          index === activeIndex
            ? 'h-1.5 w-5 bg-white'
            : 'h-1.5 w-1.5 bg-white/35 hover:bg-white/60'
        "
        :aria-label="`Ir a la tarjeta ${index + 1}`"
        @click="scrollToCard(index)"
      />
    </div>
  </div>
</template>
